import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

const Header = ({ setIsPlayerVisible }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const location = useLocation();
  const { t, language, setLanguage } = useTranslation();
  
  console.log('Header component loaded - NOW BROADCASTING VERSION');

  // Navigation items with translation keys
  const navigation = [
    { name: t('nav.home'), nameKey: 'nav.home', path: '/', icon: '🏠' },
    { 
      name: t('nav.about'), 
      nameKey: 'nav.about', 
      path: '/about', 
      icon: 'ℹ️',
      dropdown: [
        { name: t('nav.about'), path: '/about', icon: 'ℹ️' },
        { name: 'Our Staff', path: '/staff', icon: '👥' },
        { name: t('nav.gallery'), path: '/gallery', icon: '📸' }
      ]
    },
    { 
      name: t('nav.programs'), 
      nameKey: 'nav.programs', 
      path: '/programs', 
      icon: '📅',
      dropdown: [
        { name: t('nav.programs'), path: '/programs', icon: '📅' },
        { name: 'Learn Kissi', path: '/kissi-learn', icon: '🗣️' },
        { name: 'A Minute with God', path: '/a-minute-with-god', icon: '✝️' },
        { name: t('nav.clocks'), path: '/clocks', icon: '🕐' },
        { name: t('nav.podcast'), path: '/samples', icon: '🎵' },
        { name: t('nav.weatherDashboard'), path: '/weather-dashboard', icon: '🌧️' }
      ]
    },
    { name: t('nav.churchPartners'), nameKey: 'nav.churchPartners', path: '/church-partners', icon: '⛪' },
    { 
      name: t('nav.impact'), 
      nameKey: 'nav.impact', 
      path: '/impact', 
      icon: '💝',
      dropdown: [
        { name: t('nav.impact'), path: '/impact', icon: '💝' },
        { name: 'Impact Stories', path: '/impact-stories', icon: '📖' }
      ]
    },
    { 
      name: t('nav.donate'), 
      nameKey: 'nav.donate', 
      path: '/donate', 
      icon: '💖',
      dropdown: [
        { name: t('donate.generalGiving'), path: '/donate', icon: '💝' },
        { name: t('nav.majorGifts'), path: '/donate/major-gifts', icon: '🎁' }
      ]
    },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Combined top announcement — one compact bar on mobile, expanded on desktop */}
      <div className="bg-emerald-600 text-white text-center overflow-hidden">
        {/* Mobile: one tight line only */}
        <div className="md:hidden px-3 py-1.5 text-[13px] font-semibold leading-tight">
          🎙️ {t('programs.nowBroadcasting')}
        </div>
        {/* Desktop / tablet: original two-line ticker */}
        <div className="hidden md:block">
          <div style={{
            background: '#059669',
            color: '#fff',
            padding: '8px 10px',
            font: '700 15px/1.4 Inter, system-ui'
          }}>
            🎙️ {t('programs.nowBroadcasting')}
          </div>
          <div className="bg-kioo-primary text-white py-2 px-4 text-sm font-medium overflow-hidden">
            <div className="animate-pulse">
              📡 {t('programs.servingRegion')}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-lg border-b-2 border-kioo-primary sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group shrink-0">
              <div className="radio-waves">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ab37571b-81ea-4716-830b-4dd3875c42b0/artifacts/3n0kpvfn_KIOO%20RADIO.png" 
                  alt="Kioo Radio Logo" 
                  className="h-10 sm:h-12 w-auto group-hover:scale-105 transition-transform duration-200"
                />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-kioo-primary">Kioo Radio</h1>
                <p className="text-xs text-kioo-secondary -mt-1">98.1 FM - The Gift of Good News</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigation.map((item) => (
                <div key={item.path} className="relative">
                  {item.dropdown ? (
                    <div
                      className="relative group"
                      onMouseEnter={() => setDropdownOpen(item.path)}
                      onMouseLeave={() => setDropdownOpen(null)}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setDropdownOpen((prev) => (prev === item.path ? null : item.path))
                        }
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${
                          isActive(item.path)
                            ? 'bg-kioo-primary text-white shadow-lg transform scale-105'
                            : 'text-gray-700 hover:bg-kioo-primary hover:text-white hover:shadow-md hover:scale-105'
                        }`}
                        aria-haspopup="menu"
                        aria-expanded={dropdownOpen === item.path}
                      >
                        <span className="text-xs">{item.icon}</span>
                        <span data-i18n={item.nameKey}>{item.name}</span>
                        <span className="text-xs">▼</span>
                      </button>

                      {dropdownOpen === item.path && (
                        <div className="absolute top-full left-0 pt-1 w-48 z-50">
                          <div className="bg-white rounded-lg shadow-lg border border-gray-200 py-2">
                            <Link
                              to={item.path}
                              onClick={() => setDropdownOpen(null)}
                              className="block px-4 py-2 text-sm text-gray-700 hover:bg-kioo-primary hover:text-white transition-colors"
                            >
                              <span className="text-xs mr-2">{item.icon}</span>
                              {item.name}
                            </Link>
                            {item.dropdown
                              .filter((d) => d.path !== item.path)
                              .map((dropdownItem) => (
                                <Link
                                  key={dropdownItem.path}
                                  to={dropdownItem.path}
                                  onClick={() => setDropdownOpen(null)}
                                  className="block px-4 py-2 text-sm text-gray-700 hover:bg-kioo-primary hover:text-white transition-colors"
                                >
                                  <span className="text-xs mr-2">{dropdownItem.icon}</span>
                                  {dropdownItem.name}
                                </Link>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      to={item.path}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center space-x-1 ${
                        isActive(item.path)
                          ? 'bg-kioo-primary text-white shadow-lg transform scale-105'
                          : 'text-gray-700 hover:bg-kioo-primary hover:text-white hover:shadow-md hover:scale-105'
                      }`}
                    >
                      <span className="text-xs">{item.icon}</span>
                      <span data-i18n={item.nameKey}>{item.name}</span>
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* Language Toggle + Listen Live Button */}
            <div className="flex items-center space-x-2 sm:space-x-4">
              
              {/* EN/FR Language Toggle */}
              <div className="language-switcher flex items-center space-x-1">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-2 py-1 text-sm font-medium rounded ${
                    language === 'en' ? 'text-kioo-primary font-bold' : 'text-gray-600 hover:text-kioo-primary'
                  }`}
                  aria-label={t('language.selector.english', 'Switch to English')}
                >
                  EN
                </button>
                <span className="text-gray-400">|</span>
                <button
                  onClick={() => setLanguage('fr')}
                  className={`px-2 py-1 text-sm font-medium rounded ${
                    language === 'fr' ? 'text-kioo-primary font-bold' : 'text-gray-600 hover:text-kioo-primary'
                  }`}
                  aria-label={t('language.selector.french', 'Basculer en français')}
                >
                  FR
                </button>
              </div>

              {/* Listen Live Button — desktop */}
              <button
                onClick={() => setIsPlayerVisible(true)}
                className="btn-primary hidden sm:flex items-center space-x-2 text-sm"
                data-testid="header-listen-live-desktop"
              >
                <span className="w-2 h-2 bg-red-500 rounded-full live-pulse"></span>
                <span>{t('nav.listenLive')}</span>
              </button>

              {/* Listen Live — compact icon on mobile (one-tap access) */}
              <button
                onClick={() => setIsPlayerVisible(true)}
                aria-label={t('nav.listenLive')}
                data-testid="header-listen-live-mobile"
                className="sm:hidden inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-red-600 text-white text-xs font-bold shadow"
              >
                <span className="w-1.5 h-1.5 bg-white rounded-full live-pulse"></span>
                LIVE
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                data-testid="header-mobile-menu-toggle"
                className="lg:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
              >
                {isMenuOpen ? (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMenuOpen && (
            <div className="lg:hidden pb-4 border-t border-gray-200 mt-4 pt-4">
              <div className="space-y-2">
                {navigation.map((item) => (
                  <div key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => setIsMenuOpen(false)}
                      className={`flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                        isActive(item.path)
                          ? 'bg-kioo-primary text-white'
                          : 'text-gray-700 hover:bg-kioo-primary hover:text-white'
                      }`}
                      data-testid={`mobile-nav-${item.path.replace(/\//g, '-')}`}
                    >
                      <span>{item.icon}</span>
                      <span data-i18n={item.nameKey}>{item.name}</span>
                    </Link>
                    {/* Dropdown sub-items (excluding the duplicate of the parent) */}
                    {item.dropdown && (
                      <div className="ml-8 mt-1 space-y-1 border-l-2 border-gray-200 pl-3">
                        {item.dropdown
                          .filter((d) => d.path !== item.path)
                          .map((d) => (
                            <Link
                              key={d.path}
                              to={d.path}
                              onClick={() => setIsMenuOpen(false)}
                              className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                                isActive(d.path)
                                  ? 'bg-kioo-primary text-white font-medium'
                                  : 'text-gray-600 hover:bg-gray-100 hover:text-kioo-primary'
                              }`}
                              data-testid={`mobile-subnav-${d.path.replace(/\//g, '-')}`}
                            >
                              <span className="text-xs">{d.icon}</span>
                              <span>{d.name}</span>
                            </Link>
                          ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Mobile Listen Live Button */}
                <button
                  onClick={() => {
                    setIsPlayerVisible(true);
                    setIsMenuOpen(false);
                  }}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-kioo-primary text-white rounded-lg font-medium"
                >
                  <span className="w-2 h-2 bg-red-400 rounded-full live-pulse"></span>
                  <span data-i18n="listen">🔴 Listen Live</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

    </>
  );
};

export default Header;